import { useState } from 'react';
import { DesignElement } from './DesignCanvas';

interface DesignToolbarProps {
  selectedColor: { name: string; value: string; image: string };
  shirtColors: { name: string; value: string; image: string }[];
  onColorChange: (color: { name: string; value: string; image: string }) => void;
  onAddText: (text: string, style: any) => void;
  onAddImage: (imageUrl: string) => void;
  selectedElement: DesignElement | undefined;
  onElementUpdate: (id: string, updates: Partial<DesignElement>) => void;
  selectedSize: string;
  onSizeChange: (size: string) => void;
  printLocation: string;
}

const textStyles = [
  { name: 'Classic', fontFamily: 'Arial, sans-serif', fontWeight: '700', fontSize: 32, letterSpacing: '0.05em', textTransform: 'uppercase' },
  { name: 'Vintage', fontFamily: 'Georgia, serif', fontWeight: '700', fontSize: 36, letterSpacing: '0.02em', textTransform: 'uppercase' },
  { name: 'Minimal', fontFamily: 'Helvetica, sans-serif', fontWeight: '300', fontSize: 28, letterSpacing: '0.1em', textTransform: 'lowercase' },
  { name: 'Athletic', fontFamily: 'Impact, sans-serif', fontWeight: '900', fontSize: 40, letterSpacing: '0em', textTransform: 'uppercase' },
  { name: 'Script', fontFamily: 'Brush Script MT, cursive', fontWeight: '400', fontSize: 38, letterSpacing: '0em', textTransform: 'none' },
  { name: 'Bold Varsity', fontFamily: 'Arial Black, sans-serif', fontWeight: '900', fontSize: 42, letterSpacing: '0.03em', textTransform: 'uppercase' },
];

const fontFamilies = [
  { name: 'Arial', value: 'Arial, sans-serif' },
  { name: 'Georgia', value: 'Georgia, serif' },
  { name: 'Helvetica', value: 'Helvetica, sans-serif' },
  { name: 'Impact', value: 'Impact, sans-serif' },
  { name: 'Times New Roman', value: 'Times New Roman, serif' },
  { name: 'Courier', value: 'Courier New, monospace' },
  { name: 'Verdana', value: 'Verdana, sans-serif' },
  { name: 'Brush Script', value: 'Brush Script MT, cursive' },
  { name: 'Arial Black', value: 'Arial Black, sans-serif' },
];

const fontWeights = [
  { name: 'Light', value: '300' },
  { name: 'Regular', value: '400' },
  { name: 'Bold', value: '700' },
  { name: 'Black', value: '900' },
];

const sizes = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

// Print area size limits (in percentage of canvas)
const PRINT_SIZE_LIMITS = {
  'Front Center': { maxWidth: 50, maxHeight: 40 },
  'Back Center': { maxWidth: 50, maxHeight: 40 },
  'Left Chest': { maxWidth: 20, maxHeight: 20 },
  'Right Chest': { maxWidth: 20, maxHeight: 20 },
};

// Preset size options (in percentage)
const SIZE_PRESETS = {
  'Front Center': { small: 20, medium: 35, large: 50 },
  'Back Center': { small: 20, medium: 35, large: 50 },
  'Left Chest': { small: 8, medium: 12, large: 18 },
  'Right Chest': { small: 8, medium: 12, large: 18 },
};

export function DesignToolbar({
  selectedColor,
  shirtColors,
  onColorChange,
  onAddText,
  onAddImage,
  selectedElement,
  onElementUpdate,
  selectedSize,
  onSizeChange,
  printLocation,
}: DesignToolbarProps) {
  const [activeTab, setActiveTab] = useState('text');
  const [textInput, setTextInput] = useState('BULLDOGS');
  const [textColor, setTextColor] = useState('#000000');
  const [selectedStyle, setSelectedStyle] = useState(textStyles[0]);
  const [lockAspectRatio, setLockAspectRatio] = useState(true);

  const handleAddTextClick = () => {
    if (textInput.trim()) {
      onAddText(textInput, { ...selectedStyle, color: textColor });
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a PNG, JPG, or SVG file.');
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('File size must be less than 10MB.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const imageUrl = event.target?.result as string;
        onAddImage(imageUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleReplaceImage = () => {
    const input = document.getElementById('image-upload') as HTMLInputElement;
    if (input) {
      input.value = '';
      input.click();
    }
  };

  // Get current print size limits
  const currentLimits = PRINT_SIZE_LIMITS[printLocation as keyof typeof PRINT_SIZE_LIMITS] || PRINT_SIZE_LIMITS['Front Center'];
  const currentPresets = SIZE_PRESETS[printLocation as keyof typeof SIZE_PRESETS] || SIZE_PRESETS['Front Center'];

  // Convert percentage to approximate inches (assuming 12" wide canvas for front/back, 4" for chest)
  const getApproximateInches = (percentage: number, dimension: 'width' | 'height') => {
    const isChestLocation = printLocation.includes('Chest');
    const baseSize = isChestLocation ? 4 : 12;
    return ((percentage / 100) * baseSize).toFixed(1);
  };

  // Handle width change with aspect ratio lock
  const handleWidthChange = (newWidth: number) => {
    if (!selectedElement) return;
    
    // Constrain to max width
    newWidth = Math.min(newWidth, currentLimits.maxWidth);
    newWidth = Math.max(5, newWidth);

    if (lockAspectRatio && selectedElement.type === 'image') {
      const aspectRatio = selectedElement.width / selectedElement.height;
      const newHeight = newWidth / aspectRatio;
      
      // Check if new height exceeds limit
      if (newHeight > currentLimits.maxHeight) {
        const constrainedHeight = currentLimits.maxHeight;
        const constrainedWidth = constrainedHeight * aspectRatio;
        onElementUpdate(selectedElement.id, { width: constrainedWidth, height: constrainedHeight });
      } else {
        onElementUpdate(selectedElement.id, { width: newWidth, height: newHeight });
      }
    } else {
      onElementUpdate(selectedElement.id, { width: newWidth });
    }
  };

  // Handle height change with aspect ratio lock
  const handleHeightChange = (newHeight: number) => {
    if (!selectedElement) return;
    
    // Constrain to max height
    newHeight = Math.min(newHeight, currentLimits.maxHeight);
    newHeight = Math.max(5, newHeight);

    if (lockAspectRatio && selectedElement.type === 'image') {
      const aspectRatio = selectedElement.width / selectedElement.height;
      const newWidth = newHeight * aspectRatio;
      
      // Check if new width exceeds limit
      if (newWidth > currentLimits.maxWidth) {
        const constrainedWidth = currentLimits.maxWidth;
        const constrainedHeight = constrainedWidth / aspectRatio;
        onElementUpdate(selectedElement.id, { width: constrainedWidth, height: constrainedHeight });
      } else {
        onElementUpdate(selectedElement.id, { width: newWidth, height: newHeight });
      }
    } else {
      onElementUpdate(selectedElement.id, { height: newHeight });
    }
  };

  // Apply preset size
  const applyPresetSize = (preset: 'small' | 'medium' | 'large') => {
    if (!selectedElement) return;
    
    const presetWidth = currentPresets[preset];
    
    if (selectedElement.type === 'image') {
      const aspectRatio = selectedElement.width / selectedElement.height;
      const presetHeight = presetWidth / aspectRatio;
      
      // Ensure height doesn't exceed limit
      if (presetHeight > currentLimits.maxHeight) {
        const constrainedHeight = currentLimits.maxHeight;
        const constrainedWidth = constrainedHeight * aspectRatio;
        onElementUpdate(selectedElement.id, { width: constrainedWidth, height: constrainedHeight });
      } else {
        onElementUpdate(selectedElement.id, { width: presetWidth, height: presetHeight });
      }
    } else {
      // For text, adjust width and keep proportional height
      const heightRatio = selectedElement.height / selectedElement.width;
      const presetHeight = presetWidth * heightRatio;
      onElementUpdate(selectedElement.id, { width: presetWidth, height: Math.min(presetHeight, currentLimits.maxHeight) });
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {['text', 'typography', 'style', 'size-control', 'colors', 'size', 'upload'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 text-xs font-bold capitalize transition-colors whitespace-nowrap ${
              activeTab === tab
                ? 'bg-red-900 text-white'
                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            {tab === 'size-control' ? 'Design Size' : tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {/* Text Tab */}
        {activeTab === 'text' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Your Text</label>
              <input
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="Enter your text..."
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-900 focus:outline-none text-sm font-medium"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Text Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  className="w-12 h-12 rounded-lg border-2 border-gray-200 cursor-pointer"
                />
                <input
                  type="text"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-red-900 focus:outline-none text-sm font-mono"
                />
              </div>
            </div>

            <button
              onClick={handleAddTextClick}
              className="w-full bg-red-900 hover:bg-red-800 text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="ri-add-line text-lg"></i>
              Add Text to Design
            </button>

            {selectedElement?.type === 'text' && (
              <div className="pt-4 border-t border-gray-200 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase">Edit Selected Text</p>
                <input
                  type="text"
                  value={selectedElement.content}
                  onChange={(e) => onElementUpdate(selectedElement.id, { content: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-red-900 focus:outline-none text-sm font-medium"
                />
              </div>
            )}
          </div>
        )}

        {/* Typography Tab */}
        {activeTab === 'typography' && (
          <div className="space-y-4">
            {selectedElement?.type === 'text' ? (
              <>
                {/* Text Alignment */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Text Alignment</label>
                  <div className="grid grid-cols-3 gap-2">
                    {(['left', 'center', 'right'] as const).map((align) => (
                      <button
                        key={align}
                        onClick={() => onElementUpdate(selectedElement.id, { textAlign: align })}
                        className={`py-2.5 rounded-lg border-2 font-bold text-sm transition-all flex items-center justify-center gap-2 whitespace-nowrap ${
                          (selectedElement.textAlign ?? 'center') === align
                            ? 'border-red-900 bg-red-900 text-white'
                            : 'border-gray-200 text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <i className={`ri-align-${align} text-base`}></i>
                        {align.charAt(0).toUpperCase() + align.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Font Family */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Font Family</label>
                  <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto pr-2">
                    {fontFamilies.map((font) => (
                      <button
                        key={font.name}
                        onClick={() => onElementUpdate(selectedElement.id, { fontFamily: font.value })}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          selectedElement.fontFamily === font.value
                            ? 'border-red-900 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div
                          className="text-sm mb-1"
                          style={{ fontFamily: font.value }}
                        >
                          {selectedElement.content.substring(0, 8) || 'Sample'}
                        </div>
                        <div className="text-xs font-bold text-gray-500">{font.name}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Font Weight */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Font Weight</label>
                  <div className="grid grid-cols-4 gap-2">
                    {fontWeights.map((weight) => (
                      <button
                        key={weight.name}
                        onClick={() => onElementUpdate(selectedElement.id, { fontWeight: weight.value })}
                        className={`py-2.5 rounded-lg border-2 font-bold text-xs transition-all whitespace-nowrap ${
                          (selectedElement.fontWeight ?? '700') === weight.value
                            ? 'border-red-900 bg-red-900 text-white'
                            : 'border-gray-200 text-gray-700 hover:border-gray-300'
                        }`}
                        style={{ fontWeight: weight.value }}
                      >
                        {weight.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Letter Spacing */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Letter Spacing</label>
                  <input
                    type="range"
                    min="-0.05"
                    max="0.3"
                    step="0.01"
                    value={parseFloat(selectedElement.letterSpacing?.replace('em', '') ?? '0')}
                    onChange={(e) => onElementUpdate(selectedElement.id, { letterSpacing: `${e.target.value}em` })}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Tight</span>
                    <span>{parseFloat(selectedElement.letterSpacing?.replace('em', '') ?? '0').toFixed(2)}em</span>
                    <span>Wide</span>
                  </div>
                </div>

                {/* Text Outline/Stroke */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Text Outline</label>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <label className="text-xs font-semibold text-gray-600 w-16">Width:</label>
                      <input
                        type="range"
                        min="0"
                        max="4"
                        step="0.5"
                        value={selectedElement.textStroke?.width ?? 0}
                        onChange={(e) => onElementUpdate(selectedElement.id, { 
                          textStroke: { 
                            width: parseFloat(e.target.value), 
                            color: selectedElement.textStroke?.color ?? '#000000' 
                          } 
                        })}
                        className="flex-1"
                      />
                      <span className="text-xs text-gray-500 w-8">{selectedElement.textStroke?.width ?? 0}px</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="text-xs font-semibold text-gray-600 w-16">Color:</label>
                      <input
                        type="color"
                        value={selectedElement.textStroke?.color ?? '#000000'}
                        onChange={(e) => onElementUpdate(selectedElement.id, { 
                          textStroke: { 
                            width: selectedElement.textStroke?.width ?? 0, 
                            color: e.target.value 
                          } 
                        })}
                        className="w-10 h-10 rounded-lg border-2 border-gray-200 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={selectedElement.textStroke?.color ?? '#000000'}
                        onChange={(e) => onElementUpdate(selectedElement.id, { 
                          textStroke: { 
                            width: selectedElement.textStroke?.width ?? 0, 
                            color: e.target.value 
                          } 
                        })}
                        className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-red-900 focus:outline-none text-xs font-mono"
                      />
                    </div>
                  </div>
                </div>

                {/* Quick Presets */}
                <div className="pt-4 border-t border-gray-200">
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Quick Presets</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => onElementUpdate(selectedElement.id, { 
                        textStroke: { width: 2, color: '#FFFFFF' } 
                      })}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-bold transition-colors whitespace-nowrap"
                    >
                      White Outline
                    </button>
                    <button
                      onClick={() => onElementUpdate(selectedElement.id, { 
                        textStroke: { width: 2, color: '#000000' } 
                      })}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-bold transition-colors whitespace-nowrap"
                    >
                      Black Outline
                    </button>
                    <button
                      onClick={() => onElementUpdate(selectedElement.id, { 
                        textStroke: { width: 3, color: '#5C0F2E' } 
                      })}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-bold transition-colors whitespace-nowrap"
                    >
                      Maroon Outline
                    </button>
                    <button
                      onClick={() => onElementUpdate(selectedElement.id, { 
                        textStroke: { width: 0, color: '#000000' } 
                      })}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-bold transition-colors whitespace-nowrap"
                    >
                      No Outline
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <i className="ri-text text-4xl text-gray-300 mb-3"></i>
                <p className="text-sm text-gray-500 font-medium">Select a text element to edit typography</p>
                <p className="text-xs text-gray-400 mt-1">Add text from the Text tab first</p>
              </div>
            )}
          </div>
        )}

        {/* Style Tab */}
        {activeTab === 'style' && (
          <div className="space-y-4">
            <p className="text-sm font-bold text-gray-700">Choose a Text Style</p>
            <div className="grid grid-cols-2 gap-3">
              {textStyles.map((style) => (
                <button
                  key={style.name}
                  onClick={() => {
                    setSelectedStyle(style);
                    if (selectedElement?.type === 'text') {
                      onElementUpdate(selectedElement.id, {
                        fontFamily: style.fontFamily,
                        fontWeight: style.fontWeight,
                        fontSize: style.fontSize,
                        letterSpacing: style.letterSpacing,
                        textTransform: style.textTransform,
                      });
                    }
                  }}
                  className={`p-4 rounded-lg border-2 transition-all text-center ${
                    selectedStyle.name === style.name
                      ? 'border-red-900 bg-red-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div
                    className="text-lg mb-1"
                    style={{
                      fontFamily: style.fontFamily,
                      fontWeight: style.fontWeight,
                      textTransform: style.textTransform as any,
                    }}
                  >
                    {textInput || 'Sample'}
                  </div>
                  <div className="text-xs font-bold text-gray-500">{style.name}</div>
                </button>
              ))}
            </div>

            {selectedElement?.type === 'text' && (
              <div className="pt-4 border-t border-gray-200 space-y-3">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-2">Font Size</label>
                  <input
                    type="range"
                    min="16"
                    max="72"
                    value={selectedElement.fontSize || 32}
                    onChange={(e) => onElementUpdate(selectedElement.id, { fontSize: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <div className="text-xs text-gray-500 text-center mt-1">{selectedElement.fontSize || 32}px</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* NEW: Design Size Control Tab */}
        {activeTab === 'size-control' && (
          <div className="space-y-4">
            {selectedElement ? (
              <>
                {/* Current Dimensions Display */}
                <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg p-4 border border-red-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-bold text-gray-700 uppercase">Current Size</h4>
                    <span className="text-xs font-bold text-red-900 bg-white px-2 py-1 rounded">
                      {printLocation}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-lg p-3 border border-red-100">
                      <div className="text-xs text-gray-500 mb-1">Width</div>
                      <div className="text-lg font-black text-gray-900">
                        {getApproximateInches(selectedElement.width, 'width')}"
                      </div>
                      <div className="text-xs text-gray-400">{selectedElement.width.toFixed(1)}%</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-red-100">
                      <div className="text-xs text-gray-500 mb-1">Height</div>
                      <div className="text-lg font-black text-gray-900">
                        {getApproximateInches(selectedElement.height, 'height')}"
                      </div>
                      <div className="text-xs text-gray-400">{selectedElement.height.toFixed(1)}%</div>
                    </div>
                  </div>
                </div>

                {/* Preset Size Chips */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">Quick Size Presets</label>
                  <div className="grid grid-cols-3 gap-2">
                    {(['small', 'medium', 'large'] as const).map((preset) => (
                      <button
                        key={preset}
                        onClick={() => applyPresetSize(preset)}
                        className="py-3 px-2 rounded-lg border-2 border-gray-200 hover:border-red-900 hover:bg-red-50 transition-all text-center group"
                      >
                        <div className="text-xs font-bold text-gray-700 group-hover:text-red-900 capitalize mb-1">
                          {preset}
                        </div>
                        <div className="text-xs text-gray-500">
                          {getApproximateInches(currentPresets[preset], 'width')}"
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Width Slider */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-bold text-gray-700">Width</label>
                    <span className="text-xs font-bold text-gray-500">
                      Max: {getApproximateInches(currentLimits.maxWidth, 'width')}"
                    </span>
                  </div>
                  <input
                    type="range"
                    min="5"
                    max={currentLimits.maxWidth}
                    step="0.5"
                    value={selectedElement.width}
                    onChange={(e) => handleWidthChange(parseFloat(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Small</span>
                    <span className="font-bold text-gray-700">
                      {getApproximateInches(selectedElement.width, 'width')}" ({selectedElement.width.toFixed(1)}%)
                    </span>
                    <span>Max</span>
                  </div>
                </div>

                {/* Height Slider */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-bold text-gray-700">Height</label>
                    <span className="text-xs font-bold text-gray-500">
                      Max: {getApproximateInches(currentLimits.maxHeight, 'height')}"
                    </span>
                  </div>
                  <input
                    type="range"
                    min="5"
                    max={currentLimits.maxHeight}
                    step="0.5"
                    value={selectedElement.height}
                    onChange={(e) => handleHeightChange(parseFloat(e.target.value))}
                    className="w-full"
                    disabled={lockAspectRatio && selectedElement.type === 'image'}
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Small</span>
                    <span className="font-bold text-gray-700">
                      {getApproximateInches(selectedElement.height, 'height')}" ({selectedElement.height.toFixed(1)}%)
                    </span>
                    <span>Max</span>
                  </div>
                </div>

                {/* Aspect Ratio Lock (for images) */}
                {selectedElement.type === 'image' && (
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-2">
                      <i className={`ri-lock-${lockAspectRatio ? 'fill' : 'unlock-line'} text-lg ${lockAspectRatio ? 'text-red-900' : 'text-gray-400'}`}></i>
                      <span className="text-sm font-bold text-gray-700">Lock Aspect Ratio</span>
                    </div>
                    <button
                      onClick={() => setLockAspectRatio(!lockAspectRatio)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        lockAspectRatio ? 'bg-red-900' : 'bg-gray-300'
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                          lockAspectRatio ? 'translate-x-6' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                )}

                {/* Print Size Guardrails Info */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex gap-3">
                    <i className="ri-information-line text-blue-600 text-lg flex-shrink-0 mt-0.5"></i>
                    <div className="text-xs text-blue-800">
                      <p className="font-bold mb-1">Print Size Limits</p>
                      <ul className="space-y-1">
                        <li>• Front/Back prints: up to 12" wide × 16" tall</li>
                        <li>• Chest prints: up to 4" wide × 4" tall</li>
                        <li>• Sizes are approximate based on print location</li>
                        <li>• Design stays within safe print boundaries</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Position Info */}
                <div className="pt-4 border-t border-gray-200">
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Position</label>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                      <span className="text-gray-500">X:</span>
                      <span className="font-bold text-gray-900 ml-1">{selectedElement.x.toFixed(1)}%</span>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                      <span className="text-gray-500">Y:</span>
                      <span className="font-bold text-gray-900 ml-1">{selectedElement.y.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <i className="ri-ruler-line text-4xl text-gray-300 mb-3"></i>
                <p className="text-sm text-gray-500 font-medium">Select a design element to adjust size</p>
                <p className="text-xs text-gray-400 mt-1">Add text or upload an image first</p>
              </div>
            )}
          </div>
        )}

        {/* Colors Tab */}
        {activeTab === 'colors' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-3">Shirt Color</label>
              <div className="grid grid-cols-3 gap-3">
                {shirtColors.map((color) => (
                  <button
                    key={color.name}
                    onClick={() => onColorChange(color)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      selectedColor.name === color.name
                        ? 'border-red-900 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div
                      className="w-full h-12 rounded-md mb-2"
                      style={{ backgroundColor: color.value }}
                    ></div>
                    <div className="text-xs font-bold text-gray-700">{color.name}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <label className="block text-sm font-bold text-gray-700 mb-3">Popular Combos</label>
              <div className="space-y-2">
                {[
                  { name: 'Maroon + White', shirt: '#5C0F2E', text: '#FFFFFF' },
                  { name: 'White + Maroon', shirt: '#FFFFFF', text: '#5C0F2E' },
                  { name: 'Black + Maroon', shirt: '#000000', text: '#5C0F2E' },
                  { name: 'Gray + Maroon', shirt: '#9CA3AF', text: '#5C0F2E' },
                ].map((combo) => (
                  <button
                    key={combo.name}
                    onClick={() => {
                      const shirtColor = shirtColors.find((c) => c.value === combo.shirt);
                      if (shirtColor) onColorChange(shirtColor);
                      setTextColor(combo.text);
                      if (selectedElement?.type === 'text') {
                        onElementUpdate(selectedElement.id, { color: combo.text });
                      }
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-all"
                  >
                    <div className="flex gap-1">
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: combo.shirt }}></div>
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: combo.text }}></div>
                    </div>
                    <span className="text-sm font-bold text-gray-700">{combo.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Size Tab */}
        {activeTab === 'size' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-3">Select Size</label>
              <div className="grid grid-cols-4 gap-2">
                {sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => onSizeChange(size)}
                    className={`py-3 rounded-lg border-2 font-bold text-sm transition-all whitespace-nowrap ${
                      selectedSize === size
                        ? 'border-red-900 bg-red-900 text-white'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="text-xs font-bold text-gray-700 mb-3 uppercase">Size Guide</h4>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="font-bold text-gray-600">XS</span>
                  <span className="text-gray-500">Chest: 30-32"</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-bold text-gray-600">S</span>
                  <span className="text-gray-500">Chest: 34-36"</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-bold text-gray-600">M</span>
                  <span className="text-gray-500">Chest: 38-40"</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-bold text-gray-600">L</span>
                  <span className="text-gray-500">Chest: 42-44"</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-bold text-gray-600">XL</span>
                  <span className="text-gray-500">Chest: 46-48"</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-bold text-gray-600">2XL</span>
                  <span className="text-gray-500">Chest: 50-52"</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-bold text-gray-600">3XL</span>
                  <span className="text-gray-500">Chest: 54-56"</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-red-900 transition-colors cursor-pointer bg-gray-50">
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload" className="cursor-pointer block">
                <div className="w-16 h-16 mx-auto mb-3 flex items-center justify-center bg-white rounded-full shadow-sm">
                  <i className="ri-upload-cloud-line text-3xl text-gray-400"></i>
                </div>
                <p className="text-sm font-bold text-gray-700 mb-1">Upload Your Design</p>
                <p className="text-xs text-gray-500 mb-2">PNG, JPG, or SVG</p>
                <p className="text-xs text-gray-400">Max file size: 10MB</p>
              </label>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex gap-3">
                <i className="ri-information-line text-blue-600 text-lg flex-shrink-0 mt-0.5"></i>
                <div className="text-xs text-blue-800">
                  <p className="font-bold mb-1">Best Results:</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Use high-resolution images with transparent backgrounds for best print quality</li>
                    <li>PNG format recommended for transparent backgrounds</li>
                    <li>Minimum 300 DPI for sharp prints</li>
                    <li>SVG files will scale perfectly at any size</li>
                  </ul>
                </div>
              </div>
            </div>

            {selectedElement?.type === 'image' && (
              <div className="pt-4 border-t border-gray-200 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase mb-3">Image Controls</p>
                
                {/* Replace Image Button */}
                <button
                  onClick={handleReplaceImage}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-refresh-line text-base"></i>
                  Replace Image
                </button>

                {/* Image Info */}
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-gray-600 font-semibold">Width:</span>
                    <span className="text-gray-900 font-bold">{selectedElement.width.toFixed(1)}%</span>
                  </div>
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-gray-600 font-semibold">Height:</span>
                    <span className="text-gray-900 font-bold">{selectedElement.height.toFixed(1)}%</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 font-semibold">Rotation:</span>
                    <span className="text-gray-900 font-bold">{Math.round(selectedElement.rotation)}°</span>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => onElementUpdate(selectedElement.id, { rotation: 0 })}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-bold transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap"
                  >
                    <i className="ri-refresh-line text-sm"></i>
                    Reset Rotation
                  </button>
                  <button
                    onClick={() => {
                      const aspectRatio = selectedElement.width / selectedElement.height;
                      const newWidth = 30;
                      const newHeight = newWidth / aspectRatio;
                      onElementUpdate(selectedElement.id, { 
                        width: newWidth, 
                        height: newHeight,
                        x: 35,
                        y: 40
                      });
                    }}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-bold transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap"
                  >
                    <i className="ri-aspect-ratio-line text-sm"></i>
                    Reset Size
                  </button>
                </div>

                {/* Transparency Support Note */}
                {selectedElement.content.startsWith('data:image/png') && (
                  <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                    <div className="flex gap-2 items-start">
                      <i className="ri-checkbox-circle-line text-green-600 text-sm mt-0.5"></i>
                      <p className="text-xs text-green-800">
                        <span className="font-bold">PNG Detected:</span> Transparent backgrounds are fully supported
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Upload Tips */}
            {!selectedElement && (
              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs font-bold text-gray-700 mb-2">Quick Tips:</p>
                <ul className="space-y-1.5 text-xs text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-0.5"></i>
                    <span>Upload logos, graphics, or photos</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-0.5"></i>
                    <span>Drag to reposition on the shirt</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-0.5"></i>
                    <span>Resize using corner handles</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-0.5"></i>
                    <span>Rotate with the blue handle above</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-0.5"></i>
                    <span>Layer multiple images and text</span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}